import { useMemo, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../lib/store';
import type { Concept, Annotation, Note, Span } from '../types/responseTypes';
import type { HighlightedTextNew, SelectedSpan } from '../types/Text';

export interface UseNoteSectionCoreResult {
  selectedNoteId: string | null;
  annotationNumber: number;
  concept: Concept | null;
  isInAddEvidenceMode: boolean;
  stopEvidenceMode: () => void;
  highlightedTextSpans: HighlightedTextNew[];
  handleMouseUp: () => void;
  handleAttach: () => void;
  pendingSpans: Span[];
  currentSpan: SelectedSpan | null;
  setCurrentSpan: (span: SelectedSpan | null) => void;
  textRef: React.RefObject<HTMLDivElement | null>;
}

interface NoteSectionSelectors {
  selectAddEvidenceModeForConceptId: (state: any) => string | null;
  selectHighlightedTextSpans: (state: any) => HighlightedTextNew[];
  selectPendingSpansForAnnotation: (annotationId: string) => (state: any) => Span[];
  selectSelectedNoteId: (state: any) => string | null;
}

interface NoteSectionActions {
  addPendingSpan: (payload: { annotationId: string; span: Span }) => any;
  addHighlightedTextSpan: (
    payload: SelectedSpan & { annotationId: string; number?: number }
  ) => any;
  clearAddEvidenceModeForConceptId: () => any;
}

interface UseNoteSectionCoreProps {
  concepts: Concept[] | null;
  annotations: Annotation[] | null;
  selectedNote: Note | null;
  selectors: NoteSectionSelectors;
  actions: NoteSectionActions;
}

export const useNoteSectionCore = ({
  concepts,
  annotations,
  selectedNote,
  selectors,
  actions,
}: UseNoteSectionCoreProps): UseNoteSectionCoreResult => {
  const textRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useAppDispatch();

  const highlightedTextSpans = useAppSelector(selectors.selectHighlightedTextSpans);
  const selectedNoteId = useAppSelector(selectors.selectSelectedNoteId);
  const [currentSpan, setCurrentSpan] = useState<SelectedSpan | null>(null);

  const addEvidenceModeForConceptId = useAppSelector(selectors.selectAddEvidenceModeForConceptId);

  const stopEvidenceMode = () => {
    dispatch(actions.clearAddEvidenceModeForConceptId());
  };

  const concept = concepts?.find(
    (concept) => concept.conceptId === addEvidenceModeForConceptId?.split('#')[0]
  );
  // For new annotations, the ID format is "conceptId#new-conceptId", so check for that
  const annotationIdFromMode = addEvidenceModeForConceptId?.split('#')[1];
  const annotation = annotationIdFromMode?.startsWith('new-')
    ? null // New annotation, no existing annotation object
    : annotations?.find((annotation) => annotation.annotationId === annotationIdFromMode);

  const annotationNumber = useMemo(() => {
    const conceptAnnotations = annotations?.filter(
      (annotation) => annotation.conceptId === concept?.conceptId
    );
    const annotationIdx = conceptAnnotations?.findIndex(
      (annotation) => annotation.annotationId === addEvidenceModeForConceptId?.split('#')[1]
    );
    if (annotationIdx === -1) {
      const annotationsForThisConcept = annotations?.filter(
        (annotation) => annotation.conceptId === concept?.conceptId
      );
      return annotationsForThisConcept?.length ? annotationsForThisConcept.length + 1 : 1;
    }
    return annotationIdx ? annotationIdx + 1 : 1;
  }, [annotations, addEvidenceModeForConceptId, concept]);

  const annotationId = useMemo(() => {
    if (!annotation && concept) {
      // For unsaved annotations, preserve the active temp id so each new card keeps separate pending evidence.
      return annotationIdFromMode || `new-${concept.conceptId}`;
    }
    return annotation?.annotationId;
  }, [annotation, annotationIdFromMode, concept]);

  // Stable empty array reference to avoid unnecessary re-renders
  const EMPTY_SPANS_ARRAY: Span[] = [];

  const pendingSpansSelector = useMemo(
    () =>
      annotationId
        ? selectors.selectPendingSpansForAnnotation(annotationId)
        : () => EMPTY_SPANS_ARRAY,
    [annotationId]
  );
  const pendingSpans = useAppSelector(pendingSpansSelector);

  const handleMouseUp = () => {
    setCurrentSpan(null);
    if (!(addEvidenceModeForConceptId !== null)) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    if (range.startOffset === range.endOffset) return;

    const fragment = range.cloneContents();

    // Ignore the text inside the [data-ignore-offset="true"] element
    // i.e ignore the evidence counts bubble text
    const walkerSelectedTxt = document.createTreeWalker(fragment, NodeFilter.SHOW_TEXT, null);
    let selectedText = '';
    let node;
    while ((node = walkerSelectedTxt.nextNode())) {
      const parentEl = node.parentElement;
      if (parentEl?.closest('[data-ignore-offset="true"]')) {
        continue;
      }
      selectedText += node.textContent;
    }

    if (selectedText.length === 0) return;
    if (range.startContainer === range.endContainer) {
      if (
        (range.startContainer as Element).parentElement?.getAttribute('data-ignore-offset') ===
        'true'
      ) {
        return;
      }
    }

    const rect = range.getBoundingClientRect();

    const walker = document.createTreeWalker(textRef.current as Node, NodeFilter.SHOW_TEXT, null);

    let currentNode: Node | null = walker.nextNode();
    let offsetAddition = 0;
    // Walk through all the text nodes to calculate actual offset and skip evidence numbers.
    while (currentNode) {
      if (currentNode === range.startContainer) {
        break;
      }
      if (currentNode.parentElement?.getAttribute('data-ignore-offset') !== 'true') {
        offsetAddition += currentNode.textContent?.length || 0;
      }
      currentNode = walker.nextNode();
    }

    // For new annotations, use the active temp annotation id; for existing, use the actual annotationId.
    const resolvedAnnotationId =
      annotation?.annotationId ||
      annotationIdFromMode ||
      (concept ? `new-${concept.conceptId}` : '');

    const spanToAdd = {
      startOffset: range.startOffset + offsetAddition,
      endOffset: range.startOffset + offsetAddition + selectedText.length,
      text: selectedText,
      noteId: selectedNote?.noteId ?? '',
      annotationId: resolvedAnnotationId,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    };

    // Check if span already present in pending spans
    const spanAlreadyPresentInPending = pendingSpans.some((span) => {
      return (
        span.noteId === spanToAdd.noteId &&
        span.text === spanToAdd.text &&
        span.offsetStart === spanToAdd.startOffset &&
        span.offsetEnd === spanToAdd.endOffset
      );
    });

    // Check if span already exists in highlightedTextSpans (saved spans for current annotation)
    const spanAlreadyPresentInHighlights = highlightedTextSpans.some((span) => {
      return (
        span.noteId === spanToAdd.noteId &&
        span.text === spanToAdd.text &&
        span.offsetStart === spanToAdd.startOffset &&
        span.offsetEnd === spanToAdd.endOffset
      );
    });

    if (spanAlreadyPresentInPending || spanAlreadyPresentInHighlights) return;
    setCurrentSpan(spanToAdd);
  };

  const handleAttach = () => {
    if (!currentSpan) return;

    // For new annotations, use the active temp annotation id; for existing annotations, use the actual annotationId.
    const resolvedAnnotationId =
      annotation?.annotationId || annotationIdFromMode || `new-${concept?.conceptId || ''}`;
    if (!resolvedAnnotationId) return;

    // Calculate the evidence number: saved spans + pending spans + 1
    const savedSpansCount = annotation?.spans.length || 0;
    const pendingSpansCount = pendingSpans.length;
    const evidenceNumber = savedSpansCount + pendingSpansCount + 1;

    // Add to pending spans (client-side only, not saved to server yet)
    dispatch(
      actions.addPendingSpan({
        annotationId: resolvedAnnotationId,
        span: {
          noteId: currentSpan.noteId,
          offsetStart: currentSpan.startOffset,
          offsetEnd: currentSpan.endOffset,
          text: currentSpan.text,
        },
      })
    );

    // Also add to highlightedTextSpans for visual highlighting with correct number
    dispatch(
      actions.addHighlightedTextSpan({
        ...currentSpan,
        annotationId: resolvedAnnotationId,
        number: evidenceNumber,
      })
    );

    window.getSelection()?.removeAllRanges();
    setCurrentSpan(null);
  };

  return {
    annotationNumber,
    concept: concept ?? null,
    isInAddEvidenceMode: addEvidenceModeForConceptId !== null,
    stopEvidenceMode,
    highlightedTextSpans,
    handleMouseUp,
    handleAttach,
    pendingSpans,
    currentSpan,
    setCurrentSpan,
    textRef,
    selectedNoteId,
  };
};
